import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, canBillingManage } from "@/lib/billing";
import { Plus, Pencil, X, Search } from "lucide-react";
import { highlightMatch } from "@/lib/highlight";

/**
 * BillingMasters — halaman gabungan untuk Master Wilayah, Master Paket,
 * dan Assignment Kolektor. Admin/Billing only (kolektor 403 di backend).
 */
export default function BillingMasters() {
  const { user } = useAuth();
  if (!canBillingManage(user)) {
    return <div className="p-6" data-testid="billing-masters-denied">Anda tidak memiliki akses ke Master Billing.</div>;
  }
  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="billing-masters-page">
      <header>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Master Billing</h1>
        <p className="text-sm text-[#6B7280]">Wilayah, Paket, dan Assignment Kolektor.</p>
      </header>
      <Tabs defaultValue="wilayah">
        <TabsList>
          <TabsTrigger value="wilayah" data-testid="tab-wilayah">Wilayah</TabsTrigger>
          <TabsTrigger value="paket" data-testid="tab-paket">Paket</TabsTrigger>
          <TabsTrigger value="assignment" data-testid="tab-assignment">Assignment</TabsTrigger>
        </TabsList>
        <TabsContent value="wilayah"><WilayahTab /></TabsContent>
        <TabsContent value="paket"><PaketTab /></TabsContent>
        <TabsContent value="assignment"><AssignmentTab /></TabsContent>
      </Tabs>
    </div>
  );
}


function WilayahTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const { data } = await api.get("/billing/wilayah");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      <Button size="sm" onClick={() => setEditing({ isNew: true, status_aktif: true })} data-testid="wil-add-btn">
        <Plus className="w-3 h-3 mr-1" /> Tambah Wilayah
      </Button>
      <table className="w-full text-sm border-collapse">
        <thead><tr className="border-b bg-[#F8F9FA]"><th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Nama</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Aksi</th></tr></thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.wilayah_id} className="border-b" data-testid={`wil-row-${w.wilayah_id}`}>
              <td className="px-3 py-2 font-mono text-xs">{w.wilayah_id}</td>
              <td className="px-3 py-2">{w.nama_wilayah}<div className="text-xs text-[#6B7280]">{w.deskripsi}</div></td>
              <td className="px-3 py-2"><Badge className={w.status_aktif ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#E5E7EB]"}>{w.status_aktif ? "Aktif" : "Nonaktif"}</Badge></td>
              <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => setEditing({ ...w })} title="Edit Wilayah" aria-label="Edit Wilayah" data-testid={`wilayah-action-edit-${w.wilayah_id}`}><Pencil className="w-3 h-3" /></Button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-sm text-[#6B7280]">Belum ada wilayah.</td></tr>}
        </tbody>
      </table>
      {editing && <WilayahDialog editing={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    </div>
  );
}

function WilayahDialog({ editing, onClose, onSaved }) {
  const [f, setF] = useState({
    wilayah_id: editing.wilayah_id || "",
    nama_wilayah: editing.nama_wilayah || "",
    deskripsi: editing.deskripsi || "",
    status_aktif: editing.status_aktif ?? true,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const wid = (f.wilayah_id || "").trim();
    if (!wid) {
      toast.error("ID Wilayah wajib diisi");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...f, wilayah_id: wid };
      if (editing.isNew) {
        await api.post("/billing/wilayah", payload);
      } else {
        // PATCH — kirim wilayah_id juga supaya backend cek rename & cascade.
        await api.patch(`/billing/wilayah/${editing.wilayah_id}`, payload);
      }
      toast.success("Wilayah disimpan");
      await onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal simpan"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="wil-form">
        <DialogHeader><DialogTitle>{editing.isNew ? "Tambah Wilayah" : "Edit Wilayah"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <Label>ID Wilayah <span className="text-[#991B1B]">*</span></Label>
            <Input required minLength={2} maxLength={40}
                    value={f.wilayah_id}
                    onChange={(e) => setF({ ...f, wilayah_id: e.target.value.toUpperCase().replace(/[^A-Z0-9\-_]/g, "") })}
                    placeholder="WIL-000001"
                    data-testid="wil-form-id" />
            <div className="text-[10px] text-[#6B7280] mt-1">
              Wajib diinput manual. Hanya huruf, angka, "-" dan "_". Ubah ID akan memindahkan seluruh customer di wilayah ini ke ID baru; invoice historis tidak diubah.
            </div>
          </div>
          <div><Label>Nama</Label><Input required minLength={2} value={f.nama_wilayah} onChange={(e) => setF({ ...f, nama_wilayah: e.target.value })} data-testid="wil-form-nama" /></div>
          <div><Label>Deskripsi</Label><Textarea rows={2} value={f.deskripsi} onChange={(e) => setF({ ...f, deskripsi: e.target.value })} /></div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.status_aktif} onChange={(e) => setF({ ...f, status_aktif: e.target.checked })} /> Aktif</label>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Batal</Button><Button type="submit" disabled={busy} data-testid="wil-form-submit">{busy ? "…" : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function PaketTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const { data } = await api.get("/billing/packages");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3">
      <Button size="sm" onClick={() => setEditing({ isNew: true, status_aktif: true, harga: 0 })} data-testid="pkg-add-btn">
        <Plus className="w-3 h-3 mr-1" /> Tambah Paket
      </Button>
      {/* Desktop: table view — hidden pada layar <md */}
      <table className="w-full text-sm border-collapse hidden md:table">
        <thead><tr className="border-b bg-[#F8F9FA]"><th className="px-3 py-2 text-left">ID</th><th className="px-3 py-2 text-left">Nama</th><th className="px-3 py-2 text-left">Kecepatan</th><th className="px-3 py-2 text-right">Harga</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Aksi</th></tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.package_id} className="border-b" data-testid={`pkg-row-${p.package_id}`}>
              <td className="px-3 py-2 font-mono text-xs">{p.package_id}</td>
              <td className="px-3 py-2">{p.nama_paket}</td>
              <td className="px-3 py-2 text-xs">{p.kecepatan}</td>
              <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(p.harga)}</td>
              <td className="px-3 py-2"><Badge className={p.status_aktif ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#E5E7EB]"}>{p.status_aktif ? "Aktif" : "Nonaktif"}</Badge></td>
              <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" onClick={() => setEditing({ ...p })} title="Edit Paket" aria-label="Edit Paket" data-testid={`package-action-edit-${p.package_id}`}><Pencil className="w-3 h-3" /></Button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-[#6B7280]">Belum ada paket.</td></tr>}
        </tbody>
      </table>
      {/* Mobile: card view */}
      <div className="md:hidden space-y-2" data-testid="pkg-mobile-list">
        {rows.map((p) => (
          <div key={p.package_id} className="border rounded-lg bg-white p-3 shadow-sm" data-testid={`pkg-card-${p.package_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase text-[#6B7280]">{p.package_id}</div>
                <div className="font-semibold truncate">{p.nama_paket}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={p.status_aktif ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#E5E7EB]"}>{p.status_aktif ? "Aktif" : "Nonaktif"}</Badge>
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })} title="Edit Paket" aria-label="Edit Paket" data-testid={`package-action-edit-mobile-${p.package_id}`}><Pencil className="w-3 h-3" /></Button>
              </div>
            </div>
            <div className="mt-2 border-t pt-2 grid grid-cols-2 gap-y-1 text-xs">
              <div className="text-[#6B7280]">Kecepatan</div><div className="text-right">{p.kecepatan}</div>
              <div className="text-[#6B7280]">Harga</div><div className="text-right font-mono">{formatRupiah(p.harga)}</div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-6 text-center text-sm text-[#6B7280]">Belum ada paket.</div>}
      </div>
      {editing && <PaketDialog editing={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    </div>
  );
}

function PaketDialog({ editing, onClose, onSaved }) {
  const [f, setF] = useState({
    package_id: editing.package_id || "",
    nama_paket: editing.nama_paket || "", kecepatan: editing.kecepatan || "",
    harga: editing.harga || 0, deskripsi: editing.deskripsi || "", status_aktif: editing.status_aktif ?? true,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const pid = (f.package_id || "").trim();
    if (!pid) {
      toast.error("ID Paket wajib diisi");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...f, package_id: pid, harga: parseInt(f.harga, 10) || 0 };
      if (editing.isNew) {
        await api.post("/billing/packages", payload);
      } else {
        // PATCH — kirim package_id juga supaya backend cek rename & cascade.
        await api.patch(`/billing/packages/${editing.package_id}`, payload);
      }
      toast.success("Paket disimpan");
      await onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal simpan"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pkg-form">
        <DialogHeader><DialogTitle>{editing.isNew ? "Tambah Paket" : "Edit Paket"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <Label>ID Paket <span className="text-[#991B1B]">*</span></Label>
            <Input required minLength={2} maxLength={40}
                    value={f.package_id}
                    onChange={(e) => setF({ ...f, package_id: e.target.value.toUpperCase().replace(/[^A-Z0-9\-_]/g, "") })}
                    placeholder="PKG-000002"
                    data-testid="pkg-form-id" />
            <div className="text-[10px] text-[#6B7280] mt-1">
              Wajib diinput manual. Hanya huruf, angka, "-" dan "_". Ubah ID akan memindahkan seluruh customer yang memakai paket ini ke ID baru; invoice historis tidak diubah.
            </div>
          </div>
          <div><Label>Nama Paket</Label><Input required minLength={2} value={f.nama_paket} onChange={(e) => setF({ ...f, nama_paket: e.target.value })} data-testid="pkg-form-nama" /></div>
          <div><Label>Kecepatan</Label><Input required value={f.kecepatan} onChange={(e) => setF({ ...f, kecepatan: e.target.value })} placeholder="10 Mbps" /></div>
          <div><Label>Harga (Rp)</Label><Input type="number" min="0" required value={f.harga} onChange={(e) => setF({ ...f, harga: e.target.value })} data-testid="pkg-form-harga" /></div>
          <div><Label>Deskripsi</Label><Textarea rows={2} value={f.deskripsi} onChange={(e) => setF({ ...f, deskripsi: e.target.value })} /></div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.status_aktif} onChange={(e) => setF({ ...f, status_aktif: e.target.checked })} /> Aktif</label>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Batal</Button><Button type="submit" disabled={busy} data-testid="pkg-form-submit">{busy ? "…" : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Sep 2026 Phase 22n — highlight helper moved to shared /lib/highlight
// supaya konsisten dipakai di BillingCustomers & BillingInvoices juga.


function AssignmentTab() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("AKTIF");
  // Sep 2026 Phase 22l — client-side search box (nama customer /
  // customer_id / nama kolektor / username kolektor). Debounce ringan
  // via state karena set relatif kecil (max 5000 rows dari endpoint).
  const [q, setQ] = useState("");
  // Sep 2026 Phase 22m — debounce 200ms: input tetap responsif, tapi
  // filter/re-render hanya terjadi 200ms setelah keystroke terakhir.
  // Penting bila daftar assignment membesar (>10k rows).
  const [qDeb, setQDeb] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setQDeb(q), 200);
    return () => clearTimeout(h);
  }, [q]);
  const load = async () => {
    const params = statusFilter ? { status: statusFilter } : {};
    const { data } = await api.get("/billing/assignments", { params });
    setRows(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    api.get("/billing/customers", { params: { limit: 500 } }).then(({ data }) => setCustomers(data.items || [])).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => setUsers([]));
  }, []);
  const closeAssign = async (aid) => {
    if (!window.confirm("Tutup assignment ini?")) return;
    try {
      await api.post(`/billing/assignments/${aid}/close`);
      toast.success("Assignment ditutup");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal tutup"); }
  };
  const kolektorOptions = users.filter((u) => (u.extra_roles || []).includes("kolektor") || u.role === "admin");
  // Sep 2026 Phase 22m — filter memakai `qDeb` (debounced) supaya
  // re-render hanya terjadi setelah user berhenti mengetik ~200ms.
  const qNorm = qDeb.trim().toLowerCase();
  const filtered = qNorm
    ? rows.filter((a) => {
        const kol = users.find((u) => u.id === a.collector_user_id);
        const hay = [
          a.customer_name || "",
          a.customer_id || "",
          kol?.name || "",
          kol?.username || "",
          a.collector_user_id || "",
        ].join(" ").toLowerCase();
        return hay.includes(qNorm);
      })
    : rows;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" onClick={() => setCreating(true)} data-testid="ass-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Buat Assignment
        </Button>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded-md px-3 py-1 text-sm bg-white">
          <option value="AKTIF">Aktif</option>
          <option value="NONAKTIF">Nonaktif</option>
          <option value="">Semua</option>
        </select>
        <div className="relative flex-1 min-w-[220px] max-w-[420px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama customer / ID / kolektor…"
            className={`w-full border rounded-md pl-8 ${q ? "pr-9" : "pr-3"} py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#2563EB]`}
            data-testid="ass-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label="Bersihkan pencarian"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6]"
              data-testid="ass-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {qNorm && (
          <span className="text-xs text-[#6B7280]" data-testid="ass-search-count">
            {filtered.length} dari {rows.length} hasil
          </span>
        )}
      </div>
      <table className="w-full text-sm border-collapse hidden md:table">
        <thead><tr className="border-b bg-[#F8F9FA]"><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Kolektor</th><th className="px-3 py-2 text-left">Mulai</th><th className="px-3 py-2 text-left">Selesai</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Aksi</th></tr></thead>
        <tbody>
          {filtered.map((a) => {
            const kol = users.find((u) => u.id === a.collector_user_id);
            return (
              <tr key={a.id} className="border-b" data-testid={`ass-row-${a.id}`}>
                <td className="px-3 py-2">
                  <div className="text-sm font-medium text-[#111827]" data-testid={`ass-cust-name-${a.id}`}>
                    {a.customer_name
                      ? highlightMatch(a.customer_name, qNorm)
                      : <span className="text-[#9CA3AF] italic">(nama tidak ditemukan)</span>}
                  </div>
                  <div className="font-mono text-[11px] text-[#6B7280]" data-testid={`ass-cust-id-${a.id}`}>
                    {highlightMatch(a.customer_id, qNorm)}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {kol
                    ? <>{highlightMatch(kol.name, qNorm)} (@{highlightMatch(kol.username, qNorm)})</>
                    : highlightMatch(a.collector_user_id, qNorm)}
                </td>
                <td className="px-3 py-2 text-xs">{a.start_date}</td>
                <td className="px-3 py-2 text-xs">{a.end_date || "-"}</td>
                <td className="px-3 py-2"><Badge className={a.status === "AKTIF" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#E5E7EB]"}>{a.status}</Badge></td>
                <td className="px-3 py-2 text-right">
                  {a.status === "AKTIF" && (
                    <Button size="sm" variant="outline" onClick={() => closeAssign(a.id)} data-testid={`ass-close-${a.id}`}
                            title="Akhiri Assignment" aria-label="Akhiri Assignment">
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={6} className="p-6 text-center text-sm text-[#6B7280]">
              {qNorm ? `Tidak ada assignment cocok dengan "${q}".` : "Belum ada assignment."}
            </td></tr>
          )}
        </tbody>
      </table>
      {/* Mobile: assignment card view */}
      <div className="md:hidden space-y-2" data-testid="ass-mobile-list">
        {filtered.map((a) => {
          const kol = users.find((u) => u.id === a.collector_user_id);
          return (
            <div key={a.id} className="border rounded-lg bg-white p-3 shadow-sm" data-testid={`ass-card-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {a.customer_name
                      ? highlightMatch(a.customer_name, qNorm)
                      : <span className="text-[#9CA3AF] italic">(nama tidak ditemukan)</span>}
                  </div>
                  <div className="font-mono text-[11px] text-[#6B7280]">
                    {highlightMatch(a.customer_id, qNorm)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={a.status === "AKTIF" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#E5E7EB]"}>{a.status}</Badge>
                  {a.status === "AKTIF" && (
                    <Button size="sm" variant="outline" onClick={() => closeAssign(a.id)}
                            data-testid={`ass-close-mobile-${a.id}`}
                            title="Akhiri Assignment" aria-label="Akhiri Assignment">
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-2 border-t pt-2 grid grid-cols-3 gap-y-1 text-xs">
                <div className="text-[#6B7280] col-span-1">Kolektor</div>
                <div className="col-span-2 truncate">
                  {kol
                    ? <>{highlightMatch(kol.name, qNorm)} <span className="text-[#6B7280]">(@{highlightMatch(kol.username, qNorm)})</span></>
                    : highlightMatch(a.collector_user_id, qNorm)}
                </div>
                <div className="text-[#6B7280]">Mulai</div>
                <div className="col-span-2">{a.start_date}</div>
                <div className="text-[#6B7280]">Selesai</div>
                <div className="col-span-2">{a.end_date || "-"}</div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-[#6B7280]">
            {qNorm ? `Tidak ada assignment cocok dengan "${q}".` : "Belum ada assignment."}
          </div>
        )}
      </div>
      {creating && (
        <AssignmentDialog customers={customers} kolektorOptions={kolektorOptions}
                          onClose={() => setCreating(false)}
                          onSaved={async () => { setCreating(false); await load(); }} />
      )}
    </div>
  );
}

function AssignmentDialog({ customers, kolektorOptions, onClose, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [userId, setUserId] = useState("");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/billing/assignments", { customer_id: customerId, collector_user_id: userId, catatan });
      toast.success("Assignment berhasil. Assignment sebelumnya otomatis ditutup bila ada.");
      await onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal simpan"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ass-form">
        <DialogHeader><DialogTitle>Buat Assignment Kolektor</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div><Label>Customer</Label>
            <select required value={customerId} onChange={(e) => setCustomerId(e.target.value)}
                    className="border rounded-md px-3 py-2 w-full text-sm bg-white" data-testid="ass-form-customer">
              <option value="">Pilih…</option>
              {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.customer_id} — {c.nama_customer}</option>)}
            </select></div>
          <div><Label>Kolektor</Label>
            <select required value={userId} onChange={(e) => setUserId(e.target.value)}
                    className="border rounded-md px-3 py-2 w-full text-sm bg-white" data-testid="ass-form-kolektor">
              <option value="">Pilih…</option>
              {kolektorOptions.map((u) => <option key={u.id} value={u.id}>{u.name} (@{u.username})</option>)}
            </select></div>
          <div><Label>Catatan</Label><Textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Batal</Button><Button type="submit" disabled={busy}>{busy ? "…" : "Simpan"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
